import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ContractPicker.css';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  allowEmpty?: boolean;
  className?: string;
  ariaLabel?: string;
}

type PopupPosition = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

const POPUP_MAX_HEIGHT = 320;

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Поиск…',
  emptyLabel = '— выберите —',
  allowEmpty = true,
  className,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    return options.find((option) => option.value === value)?.label ?? value;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.value}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const updatePopupPosition = useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(POPUP_MAX_HEIGHT, Math.max(120, openUp ? spaceAbove : spaceBelow));

    setPopupPosition({
      left: rect.left,
      width: Math.max(rect.width, 220),
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePopupPosition();
    const onScrollOrResize = (): void => updatePopupPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePopupPosition]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
      setPopupPosition(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setPopupPosition(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const closePicker = (): void => {
    setOpen(false);
    setQuery('');
    setPopupPosition(null);
  };

  const openPicker = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const popup =
    open && popupPosition
      ? createPortal(
          <div
            ref={popupRef}
            className="contract-picker__popup contract-picker__popup--portal"
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: 'fixed',
              left: popupPosition.left,
              width: popupPosition.width,
              maxHeight: popupPosition.maxHeight,
              top: popupPosition.top,
              bottom: popupPosition.bottom,
            }}
          >
            {allowEmpty ? (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className={`contract-picker__option${!value ? ' contract-picker__option--active' : ''}`}
                onClick={() => {
                  onChange('');
                  closePicker();
                }}
              >
                <span className="contract-picker__option-num">{emptyLabel}</span>
              </button>
            ) : null}
            {filteredOptions.length === 0 ? (
              <div className="contract-picker__empty">Ничего не найдено</div>
            ) : (
              filteredOptions.map((option) => {
                const isActive = option.value === value;
                return (
                  <button
                    type="button"
                    key={option.value}
                    role="option"
                    aria-selected={isActive}
                    className={`contract-picker__option${isActive ? ' contract-picker__option--active' : ''}`}
                    onClick={() => {
                      onChange(option.value);
                      closePicker();
                    }}
                  >
                    <span className="contract-picker__option-num">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className={`contract-picker${disabled ? ' contract-picker--disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      {open ? (
        <input
          ref={inputRef}
          type="search"
          className="contract-picker__field"
          placeholder={selectedLabel || placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          aria-label={ariaLabel ?? 'Поиск в списке'}
          autoComplete="off"
        />
      ) : (
        <button
          type="button"
          className="contract-picker__trigger"
          onClick={openPicker}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          title={selectedLabel || undefined}
        >
          <span className="contract-picker__text">
            {selectedLabel || (allowEmpty ? emptyLabel : placeholder)}
          </span>
          <span className="contract-picker__caret" aria-hidden>
            ▾
          </span>
        </button>
      )}
      {popup}
    </div>
  );
};

export default SearchableSelect;
